// ============================================================
// pages/AboutPage.jsx — Connected to Firebase
// ============================================================

import React, { useState } from "react";
import { useBusinessSettings } from "../hooks/useFirestore";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase.config";

export default function AboutPage() {
  const { settings, loading } = useBusinessSettings();
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const hours = settings?.workingHours || {};
  const dayNames = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

  const handleContact = async () => {
    if (!contactForm.name || !contactForm.email || !contactForm.message) return;
    setSending(true);
    try {
      await addDoc(collection(db, "contactMessages"), {
        name: contactForm.name,
        email: contactForm.email,
        message: contactForm.message,
        isRead: false,
        createdAt: serverTimestamp(),
      });
      setContactForm({ name: "", email: "", message: "" });
      alert("Thanks for your message! We'll get back to you soon.");
    } catch (err) {
      alert("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="section-header">
        <div>
          <h2>About Chaitimecrunch</h2>
          <p>Our story, our values, our promise</p>
        </div>
      </div>

      <div className="two-col">
        <div>
          {/* Story */}
          <div className="card" style={{ marginBottom: "1.5rem" }}>
            <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "0.75rem" }}>
              {settings?.aboutTitle || "Our Story"}
            </h3>
            <p style={{ color: "var(--cocoa)", lineHeight: 1.8, fontSize: "0.92rem", whiteSpace: "pre-wrap" }}>
              {settings?.aboutText || "Born from a love of chai and freshly baked treats, Chaitimecrunch started as a home kitchen experiment in Beau Bassin. What began as weekend cookie batches for friends has blossomed into Mauritius's favourite artisan bakery. Every recipe is crafted with patience, premium ingredients, and a dash of chai-inspired magic."}
            </p>
          </div>

          {/* Contact Info */}
          <div className="card" style={{ marginBottom: "1.5rem" }}>
            <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "0.75rem" }}>Visit Us</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: "0.9rem", color: "var(--cocoa)" }}>
                📍 <span>{settings?.address || "Beau Bassin, Plaines Wilhems, Mauritius"}</span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: "0.9rem", color: "var(--cocoa)" }}>
                📞 <span>{settings?.phone || "+230 5710 0994"}</span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: "0.9rem", color: "var(--cocoa)" }}>
                ✉️ <span>{settings?.email || "hello@chaitimecrunch.mu"}</span>
              </div>
            </div>

            {/* Social Links */}
            {settings?.socialLinks && (
              <div style={{ display: "flex", gap: "0.75rem", marginTop: "1rem" }}>
                {settings.socialLinks.whatsapp && (
                  <a href={settings.socialLinks.whatsapp} target="_blank" rel="noopener noreferrer"
                    className="btn btn-sm btn-sage">WhatsApp</a>
                )}
                {settings.socialLinks.instagram && (
                  <a href={settings.socialLinks.instagram} target="_blank" rel="noopener noreferrer"
                    className="btn btn-sm btn-outline">Instagram</a>
                )}
                {settings.socialLinks.facebook && (
                  <a href={settings.socialLinks.facebook} target="_blank" rel="noopener noreferrer"
                    className="btn btn-sm btn-outline">Facebook</a>
                )}
              </div>
            )}

            <div style={{ marginTop: "1rem", padding: "1rem", background: "var(--cream)", borderRadius: 8, textAlign: "center" }}>
              <button className="btn btn-primary" onClick={() => {
                const lat = settings?.location?.latitude || -20.2333;
                const lng = settings?.location?.longitude || 57.4667;
                window.open(`https://maps.google.com/?q=${lat},${lng}`, "_blank");
              }}>
                📍 Open in Google Maps
              </button>
            </div>
          </div>

          {/* Opening Hours from Firestore */}
          <div className="card">
            <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "0.75rem" }}>Opening Hours</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              {dayNames.map((day) => {
                const h = hours[day];
                const today = new Date().toLocaleDateString("en-US", { weekday: "long" }).toLowerCase() === day;
                return (
                  <div key={day} style={{
                    display: "flex", justifyContent: "space-between", padding: "0.4rem 0.5rem",
                    borderRadius: 6, fontSize: "0.88rem",
                    background: today ? "rgba(196,136,58,0.08)" : "transparent",
                    fontWeight: today ? 700 : 400,
                  }}>
                    <span style={{ textTransform: "capitalize" }}>
                      {day} {today && "· Today"}
                    </span>
                    <span style={{ color: h?.isOpen ? "var(--sage-dark)" : "#e74c3c" }}>
                      {h?.isOpen ? `${h.open} — ${h.close}` : "Closed"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="card" style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem", textAlign: "center" }}>
            Contact Us
          </h3>
          <div className="form-group">
            <label className="form-label">Name</label>
            <input className="form-input" placeholder="Your name" value={contactForm.name}
              onChange={(e) => setContactForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" placeholder="you@email.com" value={contactForm.email}
              onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Message</label>
            <textarea className="form-textarea" placeholder="Tell us what you need..." value={contactForm.message}
              onChange={(e) => setContactForm((f) => ({ ...f, message: e.target.value }))} />
          </div>
          <button className="btn btn-primary" style={{ width: "100%" }} onClick={handleContact} disabled={sending}>
            {sending ? "Sending..." : "✉️ Send Message"}
          </button>
        </div>
      </div>
    </div>
  );
}